import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { APIWeb } from '@/api'
import RefreshList from '@/components/RefreshList'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { useAllRecordListType } from '@/hooks/listStatus'
import { formatThousands, formatDateTime } from '@/utils'
import Ripple from '@/components/Ripple'

interface DateItem {
  text: string
  value: 'one_year' | 'six_month' | 'thirty_day' | 'three_month'
}

export default React.memo(function TransactionHistory() {

  const { t } = useTranslation()
  const { getAllRecordListType } = useAllRecordListType()
  const location = useLocation()
  const coinName = location.state?.coinName
  const [selectedPeriod, setSelectedPeriod] = useState(0)
  const [data, setData] = useState<ResponsesTransactionListItem[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const pageSize = 10

  const dateItems: DateItem[] = [
    { text: t('近30天'), value: 'thirty_day' },
    { text: t('3个月'), value: 'three_month' },
    { text: t('6个月'), value: 'six_month' },
    { text: t('1年'), value: 'one_year' }
  ]

  // 通用的数据加载函数
  const fetchAssetFlowList = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading) return

    setLoading(true)
    try {
      const data = await APIWeb.getAssetFlowList({
        page,
        page_size: pageSize,
        time_type: dateItems[selectedPeriod].value as TimeType,
        type: coinName.toLowerCase()
      })

      if (data?.list) {
        const newData = data.list

        if (isAppend) {
          // 追加数据（用于加载更多）
          setData(prevData => [...prevData, ...newData])
        } else {
          // 替换数据（用于初始加载和筛选）
          setData(newData)
        }

        // 检查是否还有更多数据
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setHasMore(true)
          if (!isAppend) {
            setCurrentPage(2) // 初始加载后，下次从第2页开始
          } else {
            setCurrentPage(prev => prev + 1) // 加载更多时，页码递增
          }
        }
      } else {
        if (isAppend) {
          setHasMore(false)
        } else {
          setData([])
          setHasMore(false)
        }
      }
    } catch (error) {
      console.error('获取资产流水失败:', error)
      setHasMore(false) // 发生错误时停止加载更多
    } finally {
      setLoading(false)
    }
  }, [loading, selectedPeriod, currentPage, coinName])

  // 加载更多数据
  const loadMore = useCallback(async () => {
    await fetchAssetFlowList(currentPage, true)
  }, [fetchAssetFlowList, currentPage])

  // 下拉刷新处理函数
  const onRefresh = useCallback(async () => {
    await fetchAssetFlowList(1, false)
  }, [fetchAssetFlowList])

  // 初始加载数据
  const loadData = useCallback(async () => {
    await fetchAssetFlowList(1, false)
  }, [fetchAssetFlowList])

  // 初始化加载数据
  useEffect(() => {
    setData([])
    setCurrentPage(1)
    setHasMore(true)
    setIsFirstLoad(false)
  }, [])

  // 当选择的时间段改变时重新加载数据
  useEffect(() => {
    if (!isFirstLoad) {
      setData([])
      setCurrentPage(1)
      setHasMore(true)
      loadData()
    }
  }, [selectedPeriod, isFirstLoad])

  // 渲染单个资产流水项
  const renderItem = (item: ModelsAssetFlow, index: number) => (
    <div
      key={`${item.id}-${index}`}
      className={`px-6 min-h-18 flex items-center justify-between ${index % 2 === 1 ? 'bg-[#F5F5F5]' : 'bg-white'}`}
    >
      <div className="flex-1 flex flex-col items-baseline min-w-0 break-words">
        <div className="font-medium text-xs text-[#151515]/60 w-full">
          {getAllRecordListType(item.event || '', item.event_string || '')}
        </div>
      </div>
      <div className='flex-1 text-center text-[#151515]/60 text-xs min-w-0 break-words'>
        {formatDateTime(item.created_at)}
      </div>
      <div className="flex-1 text-right min-w-0 break-words">
        <div className={`font-semibold text-xs`}>
          {(item.change || 0) >= 0 ? '+' : ''}{formatThousands(item.change ?? 0, 2, true)} {coinName}
        </div>
      </div>
    </div>
  )
  const DateItems = useMemo(
    () => dateItems.map((item, index) => (
      <Ripple key={index} className='rounded-full flex-shrink-0'>
        <div
          onClick={() => setSelectedPeriod(index)}
          className={`px-4 py-2 rounded-full w-auto text-3.5 font-medium transition-colors whitespace-nowrap ${selectedPeriod === index
            ? 'bg-[#556AFF]/10 text-[var(--primary-color)]'
            : 'text-gray-600'
            }`}
        >
          {item.text}
        </div>
      </Ripple>
    )),
    [dateItems, selectedPeriod, t]
  )

  return (
    <PageLayout>
      <Header leftChildren={<Back />} centerChildren={<div className="text-3.75 font-medium text-black">{coinName} {t('交易记录')}</div>} />

      {/* Period Filter */}
    
      <div className='fixed w-full top-14 left-0 z-1 bg-white max-w-750px translate-x-[-50%] left-1/2'>
        <div className="flex px-6 py-4 pt-2 space-x-2 overflow-x-auto scrollbar-hide bg-white">
          {DateItems}
        </div>
        <div className="flex bg-[#F5F5F5] px-6 py-2">
          <div className="flex-1 py-2 text-left text-3.5 font-medium text-[#0F0F0F]/60">{t('类型')}</div>
          <div className="flex-1 py-2 text-center text-3.5 font-medium text-[#0F0F0F]/60">{t('日期')}</div>
          <div className="flex-1 py-2 text-right text-3.5 font-medium text-[#0F0F0F]/60">{t('数量')}</div>
        </div>
      </div>

      {/* Transaction List */}
      <div className="bg-white flex flex-col flex-1 pt-26">
        <RefreshList
          data={data}
          renderItem={renderItem}
          keyExtractor={(item, index) => `${item.token_id}-${index}`}
          loading={loading}
          hasMore={hasMore}
          onRefresh={onRefresh}
          onLoadMore={loadMore}
          emptyText={t('暂无记录')}
          finishedText={t('没有更多数据了')}
          loadingText={t('加载中')}
          className='flex-1'
        />
      </div>
    </PageLayout>
  )
})